import { ReviewPriority, ReviewRule, ReviewSettings, ReviewStatus } from "../../api/api";
import { Stepper } from "../ui/stepper";
import { Switch } from "../ui/switch";
import { CHANGED_OUTLINE, FieldError, FieldRow, FormSection, Segmented, SummaryBox, sameSet } from "./fields";
import { cn } from "@/lib/utils";

// "Gesloten" is deliberately not an option: closed vacatures never belong in a review.
const STATUS_OPTIONS: { value: ReviewStatus; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "hold", label: "On hold" },
];
const PRIORITY_OPTIONS: { value: ReviewPriority; label: string }[] = [
  { value: "hoog", label: "Hoog" },
  { value: "normaal", label: "Normaal" },
  { value: "laag", label: "Laag" },
];

type RuleKey = "primary" | "secondary";
type RuleField = "days" | "statuses" | "priorities";

function ruleChanges(a: ReviewRule, b: ReviewRule): Set<RuleField> {
  const ch = new Set<RuleField>();
  if (a.days !== b.days) ch.add("days");
  if (!sameSet(a.statuses, b.statuses)) ch.add("statuses");
  if (!sameSet(a.priorities, b.priorities)) ch.add("priorities");
  return ch;
}

// Number of changed settings, for the dialog footer's change count.
export function reviewChangeCount(cur: ReviewSettings, orig: ReviewSettings): number {
  return ruleChanges(cur.primary, orig.primary).size
    + (cur.secondary.enabled !== orig.secondary.enabled ? 1 : 0)
    + (cur.secondary.enabled ? ruleChanges(cur.secondary, orig.secondary).size : 0);
}

function ruleError(r: ReviewRule): string {
  if (r.statuses.length === 0 || r.priorities.length === 0) return "Kies minstens één status en één prioriteit.";
  return "";
}

// Mirrors the backend validation in internal/service/review.go.
export function reviewSettingsValid(s: ReviewSettings): boolean {
  return !ruleError(s.primary) && (!s.secondary.enabled || !ruleError(s.secondary));
}

function joinOr(words: string[]) {
  return words.length <= 1 ? words.join("") : `${words.slice(0, -1).join(", ")} of ${words[words.length - 1]}`;
}

function statusWords(r: ReviewRule) {
  return joinOr(STATUS_OPTIONS.filter(o => r.statuses.includes(o.value)).map(o => o.label.toLowerCase()));
}

// Empty when every priority is selected, since that isn't a restriction.
function priorityWords(r: ReviewRule) {
  if (r.priorities.length === PRIORITY_OPTIONS.length) return "";
  return joinOr(PRIORITY_OPTIONS.filter(o => r.priorities.includes(o.value)).map(o => o.label.toLowerCase()));
}

function primarySentence(r: ReviewRule, who: string, self: boolean) {
  const prio = priorityWords(r);
  return `${self ? "Je ziet" : `${who} ziet`} vacatures die ${r.days} dagen niet zijn bijgewerkt én ${statusWords(r)} staan${prio ? ` én prioriteit ${prio} hebben` : ""}.`;
}

function secondarySentence(r: ReviewRule, who: string, self: boolean) {
  const prio = priorityWords(r);
  return `Na de review ${self ? "krijg je" : `krijgt ${who}`} de vraag om ook vacatures bij te werken die ${r.days} dagen niet bijgewerkt${prio ? ` én prioriteit ${prio}` : ""} én status ${statusWords(r)} zijn. Vacatures die al in de review zaten, komen niet nog een keer.`;
}

interface Props {
  userName: string;
  isSelf: boolean;
  cur: ReviewSettings;
  orig: ReviewSettings;
  showErrors: boolean;
  onChange: (settings: ReviewSettings) => void;
}

export function UserReviewTab({ userName, isSelf, cur, orig, showErrors, onChange }: Props) {
  const who = userName || "Deze hiring manager";
  const pch = ruleChanges(cur.primary, orig.primary);
  const sch = ruleChanges(cur.secondary, orig.secondary);
  const setPrimary = (patch: Partial<ReviewRule>) => onChange({ ...cur, primary: { ...cur.primary, ...patch } });
  const setSecondary = (patch: Partial<ReviewSettings["secondary"]>) => onChange({ ...cur, secondary: { ...cur.secondary, ...patch } });

  return (
    <>
      <FormSection title="Wanneer in review">
        <SummaryBox>{primarySentence(cur.primary, who, isSelf)}</SummaryBox>
        <RuleFields
          rule={cur.primary}
          changed={pch}
          onChange={setPrimary}
          name="primary"
          daysHint="Belangrijkste regel"
        />
        <div className="text-[13px] text-waves-muted">Alle regels moeten kloppen.</div>
        {showErrors && ruleError(cur.primary) && <FieldError>{ruleError(cur.primary)}</FieldError>}
      </FormSection>

      <FormSection title="Extra vacatures (secundair)" divided>
        {cur.secondary.enabled && <SummaryBox>{secondarySentence(cur.secondary, who, isSelf)}</SummaryBox>}
        <FieldRow label="Aanbieden" hint="Na de review vragen of de hiring manager ook deze vacatures wil bijwerken">
          <div className="flex items-center gap-3 sm:pt-2">
            <Switch
              checked={cur.secondary.enabled}
              onCheckedChange={on => setSecondary({ enabled: on })}
              aria-label="Extra vacatures aanbieden"
              className={cn(cur.secondary.enabled !== orig.secondary.enabled && CHANGED_OUTLINE)}
            />
            <span className="text-sm text-waves-body">{cur.secondary.enabled ? "Aan" : "Uit"}</span>
          </div>
        </FieldRow>
        {cur.secondary.enabled && (
          <>
            <RuleFields
              rule={cur.secondary}
              changed={sch}
              onChange={setSecondary}
              name="secondary"
              priorityHint="Meerdere mogelijk"
            />
            {showErrors && ruleError(cur.secondary) && <FieldError>{ruleError(cur.secondary)}</FieldError>}
          </>
        )}
      </FormSection>
    </>
  );
}

function RuleFields({ rule, changed, onChange, name, daysHint, priorityHint }: {
  rule: ReviewRule;
  changed: Set<RuleField>;
  onChange: (patch: Partial<ReviewRule>) => void;
  name: RuleKey;
  daysHint?: string;
  priorityHint?: string;
}) {
  const prefix = name === "primary" ? "" : "Extra: ";
  return (
    <>
      <FieldRow label="Niet bijgewerkt" hint={daysHint}>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-waves-body">langer dan</span>
          <div className={cn("w-[170px] rounded-pill", changed.has("days") && CHANGED_OUTLINE)}>
            <Stepper
              value={rule.days}
              min={1}
              max={365}
              onChange={days => onChange({ days })}
              aria-label={`${prefix}Aantal dagen niet bijgewerkt`}
            />
          </div>
          <span className="text-sm text-waves-body">dagen</span>
        </div>
      </FieldRow>
      <FieldRow label="Status" hint="Van de vacature">
        <Segmented
          multiple
          aria-label={`${prefix}Status`}
          options={STATUS_OPTIONS}
          value={rule.statuses}
          onChange={statuses => onChange({ statuses })}
          changed={changed.has("statuses")}
        />
      </FieldRow>
      <FieldRow label="Prioriteit" hint={priorityHint ?? "Van de vacature"}>
        <Segmented
          multiple
          aria-label={`${prefix}Prioriteit`}
          options={PRIORITY_OPTIONS}
          value={rule.priorities}
          onChange={priorities => onChange({ priorities })}
          changed={changed.has("priorities")}
        />
      </FieldRow>
    </>
  );
}
